import { useEffect, useRef, useState } from "react";
import { getAllActiveStaffMeetings } from "../../../../services/AppService";

import { IoClose, IoTrashOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";

const AddStaffResolutionForm = ({ onSubmit, editData, onClose, open }) => {
    const { t } = useTranslation();
    const [meetingId, setMeetingId] = useState("");
    const [meetings, setMeetings] = useState([]);
    const [error, setError] = useState(null);


    const [resolutionRows, setResolutionRows] = useState([
        { id: Date.now(), agendaDescription: "", file: null, fileName: "" }
    ])

    const [formData, setFormData] = useState({
        name: "",
        meetingId: "",
        active: true
    })

    const fileInputRef = useRef(null);

    useEffect(() => {
        getAllActiveStaffMeetings().then(setMeetings).catch(console.error);
    }, []);

    useEffect(() => {
        if (editData && open) {
            setMeetingId(editData.meetingId || "");

            if (editData.resolutionItems) {
                setResolutionRows(editData.resolutionItems.map(item => ({
                    id: item.id,
                    resolutionDescription: item.resolutionDescription || "",
                    file: null,
                    fileName: item.attachmentName || ""
                })))
            }
        } else {
            setMeetingId("");
            setResolutionRows([{ id: Date.now(), resolutionDescription: "", file: null, fileName: "" }])
        }
    }, [editData, open]);

    const addRow = () => {
        setResolutionRows([...resolutionRows, { id: Date.now(), resolutionDescription: "", file: null, fileName: "" }])
    }

    const removeRow = (id) => {
        if (resolutionRows.length > 1) {
            setResolutionRows(resolutionRows.filter(row => row.id !== id));
        }
    }

    const handleRowChange = (id, field, value) => {
        setResolutionRows(prevRows => prevRows.map(row =>
            row.id === id ? { ...row, [field]: value } : row
        ));
    };

    const handleFileChange = (id, e) => {
        const file = e.target.files[0];
        if (file) {
            setResolutionRows(prevRows => prevRows.map(row =>
                row.id === id ? { ...row, file: file, fileName: file.name } : row
            ));
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!meetingId) {
            setError("Please select a meeting.");
            return;
        }

        const resolutionDto = {
            meetingId: Number(meetingId),
            items: resolutionRows.map(row => ({
                id: (row.id && row.id > 1700000000000) ? null : row.id,
                resolutionDescription: row.resolutionDescription.trim()
            }))
        }

        const finalFormData = new FormData();

        finalFormData.append(
            "resolution",
            new Blob([JSON.stringify(resolutionDto)], { type: "application/json" })
        );

        resolutionRows.forEach((row) => {
            if (row.file) {
                finalFormData.append("files", row.file);
            } else {
                finalFormData.append("files", new Blob([], { type: "application/octet-stream" }));
            }
        });

        onSubmit(finalFormData);
    };

    const formFields = [
        { name: "name", label: "Meeting Title", type: "text", placeholder: "Enter Title" },
        { name: "meetingId", label: "Meeting", type: "text", placeholder: "Select Meeting" },
    ];

    if (!open) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '900px', maxWidth: '95%' }}>
                <div className="staff-modal-header">
                    <div className="staff-modal-title">
                        {editData ? t('pageTitles.editResolution') : t('pageTitles.addResolution')}
                    </div>
                    <button className="admin-modal-close" onClick={onClose}>
                        <IoClose />
                    </button>
                </div>

                <div className="staff-form-body">
                    <form onSubmit={handleSubmit}>
                        {/* MEETING SELECT */}
                        <div className="form-group mb-4">
                            <label className="staff-modal-label">{t('tables.meetings.meeting')} <span className="text-red-500">*</span></label>
                            <select
                                value={meetingId}
                                onChange={(e) => setMeetingId(e.target.value)}
                                className="staff-modal-input"
                                required
                            >
                                <option value="">Select Meeting</option>
                                {meetings.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        </div>

                        <div className="mb-2">
                            <button type="button" onClick={addRow} className="staff-btn-save text-sm py-1">
                                {t('forms.resolution.addResolution')}
                            </button>
                        </div>

                        {/* DYNAMIC TABLE */}
                        <table className="w-full border text-sm agenda-table">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border p-2 w-1/2">{t('forms.resolution.resolution')}</th>
                                    <th className="border p-2 w-1/3">{t('common.attachments')}</th>
                                    <th className="border p-2">{t('common.delete')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resolutionRows.map((row) => (
                                    <tr key={row.id}>
                                        <td className="border p-2">
                                            <textarea
                                                className="staff-modal-input w-full h-20"
                                                placeholder="Enter Resolution"
                                                value={row.resolutionDescription}
                                                onChange={(e) => handleRowChange(row.id, 'resolutionDescription', e.target.value)}
                                                required
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <div className="flex flex-col gap-1">
                                                <input
                                                    type="file"
                                                    onChange={(e) => handleFileChange(row.id, e)}
                                                    className="text-xs"
                                                />
                                                {row.fileName && <span className="text-blue-600 text-xs">{row.fileName}</span>}
                                            </div>
                                        </td>
                                        <td className="border p-2 text-center">
                                            <button
                                                type="button"
                                                onClick={() => removeRow(row.id)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <IoTrashOutline size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="flex justify-center mt-6">
                            <button type="submit" className="staff-btn-save px-10">{t('common.save')}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddStaffResolutionForm;

